/**
 * 姓名综合评分引擎（浏览器端）
 *
 * shunshi-naming-core 依赖 node:fs/node:zlib 无法进浏览器，本文件把其
 * scoreName 的四维评分模型原样移植：同样的档位分表、维度权重与
 * banker's rounding（pyRound），查表数据来自 scripts/extract-naming-data.mjs
 * 预提取的 score-tables.json（生肖宜忌由 shunshi 真实引擎预计算），
 * 保证与原引擎输出一致。
 *
 * 四维：五格数理 0.28 / 三才配置 0.17 / 八字喜用 0.35 / 生肖 0.20；
 * 数理 = 人格40% + 总格25% + 地格20% + 外格15%（天格随姓、不计分）。
 * 八字喜用需要完整排盘，前端不臆算，恒为 null（AI 报告承担解读）。
 */
import scoreTables from '@/data/naming/score-tables.json'

const SHULI_PTS = { 大吉: 100, 吉: 85, 半吉: 60, 凶: 30, 大凶: 12 }
const SANCAI_PTS = { 大吉: 95, 吉: 85, 半吉: 60, 凶: 35, 大凶: 20 }
const ZODIAC_PTS = { 1: 100, 2: 65, 3: 55, 4: 30 } /* 宜/平/参半/忌 */
const VERDICT_NAME = { 1: '宜', 2: '平', 3: '参半', 4: '忌' }
const DIM_WEIGHTS = { shuli: 0.28, sancai: 0.17, bazi: 0.35, zodiac: 0.2 }

/** Python 风格取整（.5 取偶），与 shunshi 引擎逐分对齐 */
export function pyRound(x) {
  const floor = Math.floor(x)
  const diff = x - floor
  if (diff > 0.5) return floor + 1
  if (diff < 0.5) return floor
  return floor % 2 === 0 ? floor : floor + 1
}

/** 数理档位：>81 归 1-81（80 循环），查 81 数理表 */
const shuliLevel = (n) => scoreTables.shuli[String(n > 81 ? n - 80 : n)] || '平'

/** 生肖在 zodiacOrder 中的序号（接受 鼠/子 等生肖或地支表述） */
const ZODIAC_ALIAS = { 龍: '龙', 馬: '马', 雞: '鸡', 豬: '猪', 鷄: '鸡' }
const ZHI_TO_ZODIAC = {}
for (let i = 0; i < scoreTables.zodiacOrder.length; i++) {
  ZHI_TO_ZODIAC['子丑寅卯辰巳午未申酉戌亥'[i]] = scoreTables.zodiacOrder[i]
}
export function normalizeZodiac(z) {
  if (!z) return null
  const s = ZODIAC_ALIAS[z.trim()] || z.trim()
  if (scoreTables.zodiacOrder.includes(s)) return s
  return ZHI_TO_ZODIAC[s] || null
}

/**
 * 由五格/三才/用字计算四维分
 * @param {object} p
 * @param {{tianGe:number,renGe:number,diGe:number,waiGe:number,zongGe:number}} p.grids 五格（>81 需先归一）
 * @param {string} p.sancaiCombo 三才组合，如 '木木火'
 * @param {string[]} p.givenChars 名字用字（生肖维度只评名字，姓氏不可改不评）
 * @param {string|null} p.zodiac 生肖，如 '龙'（无则该维度为 null）
 */
export function scoreNameByGrids({ grids, sancaiCombo, givenChars = [], zodiac = null }) {
  const gridLevels = {
    tianGe: shuliLevel(grids.tianGe),
    renGe: shuliLevel(grids.renGe),
    diGe: shuliLevel(grids.diGe),
    waiGe: shuliLevel(grids.waiGe),
    zongGe: shuliLevel(grids.zongGe)
  }
  const shuli = pyRound(
    SHULI_PTS[gridLevels.renGe] * 0.4 +
      SHULI_PTS[gridLevels.zongGe] * 0.25 +
      SHULI_PTS[gridLevels.diGe] * 0.2 +
      SHULI_PTS[gridLevels.waiGe] * 0.15
  )
  const sancaiLevel = scoreTables.sancai[sancaiCombo] || null
  const sancai = sancaiLevel ? SANCAI_PTS[sancaiLevel] : null

  let zodiacScore = null
  const zodiacDetail = []
  const z = normalizeZodiac(zodiac)
  if (z) {
    const idx = scoreTables.zodiacOrder.indexOf(z)
    const judged = givenChars.filter(Boolean).map((ch) => {
      const code = scoreTables.zodiac[ch] ? Number(scoreTables.zodiac[ch][idx]) : 2
      return { char: ch, verdict: VERDICT_NAME[code] || '平', code }
    })
    zodiacScore = judged.length
      ? pyRound(judged.reduce((a, d) => a + ZODIAC_PTS[d.code], 0) / judged.length)
      : 65
    zodiacDetail.push(...judged)
  }

  const parts = [['shuli', shuli, DIM_WEIGHTS.shuli]]
  if (sancai !== null) parts.push(['sancai', sancai, DIM_WEIGHTS.sancai])
  if (zodiacScore !== null) parts.push(['zodiac', zodiacScore, DIM_WEIGHTS.zodiac])
  const totalW = parts.reduce((a, [, , w]) => a + w, 0)
  const total = pyRound(parts.reduce((a, [, v, w]) => a + v * w, 0) / totalW)

  return {
    scores: { shuli, sancai, bazi: null, zodiac: zodiacScore, total },
    gridLevels,
    sancaiLevel,
    zodiac: z,
    zodiacDetail
  }
}

export default { pyRound, normalizeZodiac, scoreNameByGrids }
